<template>
  <div v-if="visible" class="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
    <div :class="['w-full max-w-md p-6 border-4 transition-all',
                  isEasy ? 'bg-amber-50 border-amber-800' : 'bg-slate-800 border-amber-600']"
         style="border-radius: 8px;">

      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <h3 :class="['text-2xl font-serif-display', isEasy ? 'ink-text' : 'text-amber-100']">
          {{ step === 'email' ? (purpose === 'register' ? 'Create Account' : 'Verify Identity') : 'Enter Code' }}
        </h3>
        <button @click="$emit('close')"
                :class="['text-2xl hover:opacity-70', isEasy ? 'text-amber-900' : 'text-amber-300']">
          &times;
        </button>
      </div>

      <!-- Email Step -->
      <div v-if="step === 'email'" class="space-y-4">
        <p :class="['font-serif-body text-sm', isEasy ? 'text-stone-700' : 'text-stone-300']">
          {{
            purpose === 'register'
                ? 'Enter your email to create an account and save your scores.'
                : 'This name is registered. Enter the account email to continue.'
          }}
        </p>

        <input
            v-model="email"
            type="email"
            placeholder="your@email.com"
            :class="['w-full p-4 border-2 font-serif-body',
                   isEasy ? 'bg-white border-amber-700 text-stone-900' : 'bg-slate-700 border-amber-600 text-white']"
            style="border-radius: 6px;"
            @keyup.enter="requestCode"
        />

        <p v-if="error" class="text-red-500 text-sm font-serif-body">{{ error }}</p>

        <button
            @click="requestCode"
            :disabled="loading || !email"
            :class="['w-full p-4 font-serif-display font-bold transition-all border-3',
                   isEasy ? 'bg-amber-700 border-amber-900 text-white' : 'bg-amber-600 border-amber-800 text-white',
                   (loading || !email) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-amber-600']"
            style="border-radius: 6px;"
        >
          {{ loading ? 'Sending...' : 'Send Verification Code' }}
        </button>
      </div>

      <!-- Code Step -->
      <div v-if="step === 'code'" class="space-y-4">
        <p :class="['font-serif-body text-sm', isEasy ? 'text-stone-700' : 'text-stone-300']">
          We sent a 6-digit code to <strong>{{ email }}</strong>
        </p>

        <div class="flex justify-center gap-2">
          <input
              v-for="i in 6"
              :key="i"
              :ref="el => { if (el) codeInputs[i-1] = el }"
              v-model="codeDigits[i-1]"
              type="text"
              maxlength="1"
              inputmode="numeric"
              :class="['w-12 h-14 text-center text-2xl font-serif-display border-2',
                     isEasy ? 'bg-white border-amber-700 text-stone-900' : 'bg-slate-700 border-amber-600 text-white']"
              style="border-radius: 6px;"
              @input="handleCodeInput(i-1)"
              @keydown.backspace="handleBackspace(i-1, $event)"
              @paste="handlePaste"
          />
        </div>

        <p :class="['text-xs font-serif-body text-center', isEasy ? 'text-stone-500' : 'text-stone-400']">
          Code expires in {{ expiryDisplay }}
        </p>

        <p v-if="error" class="text-red-500 text-sm font-serif-body text-center">{{ error }}</p>

        <button
            @click="verifyCode"
            :disabled="loading || codeDigits.join('').length < 6"
            :class="['w-full p-4 font-serif-display font-bold transition-all border-3',
                   isEasy ? 'bg-amber-700 border-amber-900 text-white' : 'bg-amber-600 border-amber-800 text-white',
                   (loading || codeDigits.join('').length < 6) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-amber-600']"
            style="border-radius: 6px;"
        >
          {{ loading ? 'Verifying...' : 'Verify' }}
        </button>

        <button
            @click="step = 'email'; error = ''"
            :class="['w-full p-2 font-serif-body text-sm underline', isEasy ? 'text-amber-800' : 'text-amber-300']"
        >
          Use different email
        </button>
      </div>

    </div>
  </div>
</template>

<script setup>
import {computed, onUnmounted, ref, watch} from 'vue'
import {authService} from '../services/authService'

const props = defineProps({
  visible: Boolean,
  isEasy: Boolean,
  purpose: {type: String, default: 'login'}, // 'login' or 'register'
  playerName: {type: String, default: ''},
})

const emit = defineEmits(['close', 'success'])

const step = ref('email')
const email = ref('')
const codeDigits = ref(['', '', '', '', '', ''])
const codeInputs = ref([])
const loading = ref(false)
const error = ref('')
const expiryTime = ref(600)

const expiryDisplay = computed(() => {
  const mins = Math.floor(expiryTime.value / 60)
  const secs = expiryTime.value % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
})

// Reset when modal opens
watch(() => props.visible, (visible) => {
  if (visible) {
    step.value = 'email'
    email.value = ''
    codeDigits.value = ['', '', '', '', '', '']
    error.value = ''
    expiryTime.value = 600
  }
})

// Countdown timer
let timerInterval = null
watch(step, (newStep) => {
  if (newStep === 'code') {
    expiryTime.value = 600
    timerInterval = setInterval(() => {
      expiryTime.value--
      if (expiryTime.value <= 0) {
        clearInterval(timerInterval)
        error.value = 'Code expired. Please request a new one.'
      }
    }, 1000)
  } else {
    if (timerInterval) {
      clearInterval(timerInterval)
      timerInterval = null
    }
  }
})

onUnmounted(() => {
  if (timerInterval) {
    clearInterval(timerInterval)
  }
})

const requestCode = async () => {
  if (!email.value) return

  loading.value = true
  error.value = ''

  try {
    await authService.requestCode(email.value, props.purpose, props.playerName)
    step.value = 'code'
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to send code'
  } finally {
    loading.value = false
  }
}

const handleCodeInput = (index) => {
  // Only allow digits
  codeDigits.value[index] = codeDigits.value[index].replace(/\D/g, '')

  // Auto-advance to next input
  if (codeDigits.value[index] && index < 5) {
    codeInputs.value[index + 1]?.focus()
  }

  // Auto-submit when complete
  if (codeDigits.value.join('').length === 6) {
    verifyCode()
  }
}

const handleBackspace = (index, event) => {
  if (!codeDigits.value[index] && index > 0) {
    event.preventDefault()
    codeInputs.value[index - 1]?.focus()
  }
}

const handlePaste = (event) => {
  event.preventDefault()
  const pastedData = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
  if (pastedData) {
    for (let i = 0; i < 6; i++) {
      codeDigits.value[i] = pastedData[i] || ''
    }
    if (pastedData.length === 6) {
      verifyCode()
    }
  }
}

const verifyCode = async () => {
  const code = codeDigits.value.join('')
  if (code.length < 6) return

  loading.value = true
  error.value = ''

  try {
    const result = await authService.verifyCode(email.value, code)
    emit('success', result)
  } catch (err) {
    error.value = err.response?.data?.error || 'Invalid code'
    // Clear code inputs on error
    codeDigits.value = ['', '', '', '', '', '']
    codeInputs.value[0]?.focus()
  } finally {
    loading.value = false
  }
}
</script>
